// Favicon-Extraktion aus URL
export const extractFaviconFromUrl = (url) => {
  if (!url) return null;
  
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname;
    
    // Standard Favicon-URLs probieren
    const faviconUrls = [
      `${urlObj.protocol}//${domain}/favicon.ico`,
      `${urlObj.protocol}//${domain}/favicon.png`,
      `${urlObj.protocol}//${domain}/assets/favicon.ico`,
      `${urlObj.protocol}//${domain}/images/favicon.ico`,
      `${urlObj.protocol}//${domain}/img/favicon.ico`,
      // Google Favicon Service als Fallback
      `https://www.google.com/s2/favicons?domain=${domain}&sz=64`
    ];
    
    return faviconUrls;
  } catch (error) {
    console.error('Error extracting favicon from URL:', error);
    return [`https://www.google.com/s2/favicons?domain=${url}&sz=64`]; // Fallback
  }
};

// Favicon URL testen und erste verfügbare zurückgeben
export const getFaviconUrl = async (baseUrl) => {
  const faviconUrls = extractFaviconFromUrl(baseUrl);
  if (!faviconUrls) return null;
  
  for (const faviconUrl of faviconUrls) {
    try {
      const response = await fetch(faviconUrl, { method: 'HEAD', mode: 'no-cors' });
      // Bei no-cors können wir den Status nicht prüfen, aber es wirft keinen Fehler wenn die URL existiert
      return faviconUrl;
    } catch (error) {
      console.log(`Favicon nicht gefunden: ${faviconUrl}`);
    }
  }
  
  // Fallback: Google Favicon Service
  try {
    const urlObj = new URL(baseUrl);
    return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=64`;
  } catch (error) {
    return null;
  }
};

// API-Service für alle Backend-Aufrufe
export const apiCall = async (endpoint, method = 'GET', data = null, user = null) => {
  try {
    console.log(`API Call: ${method} ${endpoint}`);
    
    const config = {
      method,
      headers: {
        'Content-Type': 'application/json',
      }
    };
    
    if (user?.token && !user.token.startsWith('mock-')) {
      config.headers['Authorization'] = `Bearer ${user.token}`;
    }
    
    if (data) {
      config.body = JSON.stringify(data);
    }
    
    const response = await fetch(`/api${endpoint}`, config);
    
    if (!response.ok) {
      // Server-Fehlermeldung durchreichen (Reviewer 28.07.): das Backend liefert bewusst
      // formulierte deutsche Meldungen ({error}/{message}, z. B. 409 der Statusmaschine) —
      // die duerfen nicht hinter "HTTP error! status: 409" verschwinden.
      let serverMessage = '';
      try {
        const body = await response.json();
        serverMessage = body?.error || body?.message || '';
      } catch (_e) { /* kein JSON-Body */ }
      throw new Error(serverMessage || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API call failed:', error);
    
    if (user?.token?.startsWith('mock-')) {
      return mockApiResponse(endpoint, method, data);
    }
    
    throw error;
  }
};

// ==================== LICENSE ====================

/**
 * GET /license
 * Liefert den aktuellen Lizenz-Status: welche Module lizenziert sind und den Modus.
 * Erfordert gueltiges JWT (kein Admin-Guard).
 * @param {Object} user - { token, ... }
 * @returns {Promise<{ licensedModules: string[], mode: 'ok'|'grace', checkedAt: number }>}
 */
export const getLicense = async (user) => {
  return apiCall('/license', 'GET', null, user);
};

/**
 * PATCH Inline-Edit eines internen Katalogprodukts (F8) — nur die uebergebenen
 * Basis-Felder. Erfordert Recht can_edit_catalog (oder Admin); SAP-Material
 * (source='sap') nur Status. Kind-Objekte bleiben unberuehrt.
 * @param {Object} user
 * @param {number|string} shopId
 * @param {number|string} productId
 * @param {Object} fields - z.B. { name, description, price }
 */
export const updateCatalogProductInline = async (user, shopId, productId, fields) =>
  apiCall(`/admin/internal-shops/${shopId}/products/${productId}`, 'PATCH', fields, user);

// ==================== USER PREFERENCES ====================

/**
 * GET /user-preferences/:key
 * Liest eine User-Einstellung (key-scoped, JWT-auth).
 * @param {Object} user
 * @param {string} key  z.B. 'product_filters'
 * @returns {Promise<{ key: string, value: any }>}  value = gepartes JSON | null
 */
export const getUserPreference = async (user, key) => {
  return apiCall(`/user-preferences/${encodeURIComponent(key)}`, 'GET', null, user);
};

/**
 * PUT /user-preferences/:key
 * Speichert eine User-Einstellung (UPSERT, key-scoped, JWT-auth).
 * @param {Object} user
 * @param {string} key   z.B. 'product_filters'
 * @param {any}    value beliebiger JSON-Wert
 * @returns {Promise<{ success: boolean }>}
 */
export const setUserPreference = async (user, key, value) => {
  return apiCall(`/user-preferences/${encodeURIComponent(key)}`, 'PUT', { value }, user);
};

// ==================== FIELD MAPPINGS ====================

// ==================== RELEASE STRATEGIES ====================

/**
 * GET /release-strategies
 * Gibt alle aktiven Freigabestrategien inkl. Stufen zurück.
 * @param {Object} user
 * @returns {Promise<Array<{
 *   id: number, release_group: string, strategy_code: string, description: string,
 *   value_from: number, value_to: number|null, currency: string, active: boolean, sort_order: number,
 *   steps: Array<{ seq: number, release_code: string, code_label: string }>
 * }>>}
 */
export const getReleaseStrategies = async (user) => {
  return apiCall('/release-strategies', 'GET', null, user);
};

// ==================== SAVED CARTS ====================

export const getSavedCarts = async (user) => {
  return apiCall('/saved-carts', 'GET', null, user);
};

export const createSavedCart = async (user, payload) => {
  return apiCall('/saved-carts', 'POST', payload, user);
};

export const updateSavedCart = async (user, id, fields) => {
  return apiCall(`/saved-carts/${id}`, 'PUT', fields, user);
};

export const deleteSavedCart = async (user, id) => {
  return apiCall(`/saved-carts/${id}`, 'DELETE', null, user);
};

// ==================== LAGER-MODUL (Phase 1) ====================

/**
 * GET /lager/werke
 * Liefert die für den User erlaubten Werke und das Standard-Werk.
 * @param {Object} user
 * @returns {Promise<{ werke: Array<{werk:string, name:string}>, default_werk: string }>}
 */
export const getLagerWerke = async (user) => {
  return apiCall('/lager/werke', 'GET', null, user);
};

/**
 * GET /lager/filter-options?werk=
 * Liefert Warengruppen, Materialarten und Lagerorte für das gewählte Werk.
 * @param {Object} user
 * @param {string} werk
 * @returns {Promise<{ warengruppen: Array, materialarten: Array, lagerorte: Array }>}
 */
export const getLagerFilterOptions = async (user, werk) => {
  const qs = new URLSearchParams();
  if (werk) qs.set('werk', werk);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/lager/filter-options${query}`, 'GET', null, user);
};

/**
 * GET /lager/durchlaufzeiten?werk= — geplante Lieferzeiten-Analyse (je Warengruppe + Lieferant).
 * @param {Object} user
 * @param {string} werk
 */
export const getLagerDurchlaufzeiten = async (user, werk) => {
  const qs = werk ? `?werk=${encodeURIComponent(werk)}` : '';
  return apiCall(`/lager/durchlaufzeiten${qs}`, 'GET', null, user);
};

/**
 * GET /lager/materials
 * Suche/Filtern von Lagermaterialien mit Paginierung und Sortierung.
 * @param {Object} user
 * @param {{
 *   werk?: string,
 *   search?: string,
 *   warengruppe?: string,   // CSV: matkl-Codes
 *   materialart?: string,   // CSV: Codes
 *   bestandsstatus?: 'mit'|'ohne'|'alle',
 *   sort?: string,          // "col:asc" | "col:desc"
 *   page?: number,
 *   per_page?: number
 * }} params
 * @returns {Promise<{ items: Array, total: number, page: number, per_page: number, total_pages: number }>}
 */
export const getLagerMaterials = async (user, params = {}) => {
  const qs = new URLSearchParams();
  if (params.werk) qs.set('werk', params.werk);
  if (params.search) qs.set('search', params.search);
  if (params.warengruppe) qs.set('warengruppe', params.warengruppe);
  if (params.materialart) qs.set('materialart', params.materialart);
  if (params.bestandsstatus) qs.set('bestandsstatus', params.bestandsstatus);
  // Phase 3b — erweiterte Filter
  if (params.lagerort) qs.set('lagerort', params.lagerort);
  if (params.beschaffungsart) qs.set('beschaffungsart', params.beschaffungsart);
  if (params.dispomerkmal) qs.set('dispomerkmal', params.dispomerkmal);
  if (params.bestand_min != null && params.bestand_min !== '') qs.set('bestand_min', String(params.bestand_min));
  if (params.bestand_max != null && params.bestand_max !== '') qs.set('bestand_max', String(params.bestand_max));
  if (params.offene_banf && params.offene_banf !== 'alle') qs.set('offene_banf', params.offene_banf);
  if (params.offene_bestellung && params.offene_bestellung !== 'alle') qs.set('offene_bestellung', params.offene_bestellung);
  if (params.handlungsbedarf === true || params.handlungsbedarf === 'true') qs.set('handlungsbedarf', 'true');
  // Bestands-zentrierte Filter (Sonderbestandsart/Lieferant/Einkäufergruppe/Bestandsqualität)
  if (params.stock_type) qs.set('stock_type', params.stock_type);
  if (params.vendor) qs.set('vendor', params.vendor);
  if (params.ekgrp) qs.set('ekgrp', params.ekgrp);
  if (params.sperrbestand === true || params.sperrbestand === 'true') qs.set('sperrbestand', 'true');
  if (params.qmbestand === true || params.qmbestand === 'true') qs.set('qmbestand', 'true');
  // Dashboard Drill-down — kritischer Bestand
  if (params.kritisch === true || params.kritisch === 'true') qs.set('kritisch', 'true');
  // Phase 3c — Klassifizierungs-Merkmale (Array -> JSON.stringify -> URL-enkodiert)
  if (Array.isArray(params.merkmale) && params.merkmale.length > 0) {
    qs.set('merkmale', JSON.stringify(params.merkmale));
  }
  if (params.sort) qs.set('sort', params.sort);
  if (params.page != null) qs.set('page', String(params.page));
  if (params.per_page != null) qs.set('per_page', String(params.per_page));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/lager/materials${query}`, 'GET', null, user);
};

/**
 * GET /lager/materials/:matnr?werk=
 * Detaildaten eines Materials (Stammdaten, Bestand je Lagerort, Preisinfo).
 * @param {Object} user
 * @param {string} matnr - Materialnummer (wird URL-enkodiert)
 * @param {string} werk  - Werk
 * @returns {Promise<{ material: Object, stock: Array, stock_total: Object,
 *   meldebestand: number, sicherheitsbestand: number,
 *   letzter_preis: number, letzter_preis_waehrung: string, letzter_lieferant: string }>}
 */
export const getLagerMaterialDetail = async (user, matnr, werk) => {
  const qs = new URLSearchParams();
  if (werk) qs.set('werk', werk);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/lager/materials/${encodeURIComponent(matnr)}${query}`, 'GET', null, user);
};

/**
 * GET /purchase-requests — BANFs des Users (rollen-gescoped: admin=alle,
 * supervisor=Kostenstelle, user=eigene). Für die Lager-Sektion "BANF-Status".
 * @param {Object} user
 * @returns {Promise<Array>}
 */
export const getPurchaseRequests = async (user) => {
  return apiCall('/purchase-requests', 'GET', null, user);
};

/**
 * POST /purchase-requests/:id/retry-sap — fehlgeschlagene SAP-Uebertragung erneut anstossen.
 * Nur Admin/Supervisor, nur fuer genehmigte Vorgaenge. Idempotent: ein bereits gebuchter Vorgang
 * liefert dieselbe BANFN zurueck, statt ein zweites Mal zu buchen.
 * @param {Object} user
 * @param {number} requestId
 * @returns {Promise<{success:boolean, sap:{transferStatus:string, banfn:string|null, message:string}}>}
 */
export const retryBanfSapTransfer = async (user, requestId) => {
  return apiCall(`/purchase-requests/${encodeURIComponent(requestId)}/retry-sap`, 'POST', {}, user);
};

/**
 * GET /purchase-requests/:id/sap — Live-Sicht aus SAP auf die angelegte BANF
 * (Positionen + Kontierung inkl. BANPR/FRGKZ/FRGZU/EBELN fuer den Lebenszyklus).
 * @param {Object} user
 * @param {number} requestId
 * @returns {Promise<{transfer:Object, banf:Object|null}>}
 */
export const getBanfSapDetail = async (user, requestId) => {
  return apiCall(`/purchase-requests/${encodeURIComponent(requestId)}/sap`, 'GET', null, user);
};

/**
 * POST /cart — Lager-Schnell-BANF: fuegt einen Artikel direkt in den Warenkorb ein.
 * Identisches Datenformat wie addToCart in App.js (kein Proxy noetig).
 * @param {Object} user
 * @param {Object} cartItem - Warenkorb-Position (description, quantity, price, unit, ...)
 * @returns {Promise<Object>} Backend-Antwort
 */
export const addLagerCartItem = async (user, cartItem) => {
  return apiCall('/cart', 'POST', cartItem, user);
};

// ==================== LAGER-MODUL (Phase 3a) ====================

/**
 * GET /lager/materials/:matnr/suppliers?werk=
 * Lieferanten & Infosatz-Konditionen fuer ein Material.
 * @param {Object} user
 * @param {string} matnr - Materialnummer (wird URL-enkodiert)
 * @param {string} werk  - Werk
 * @returns {Promise<Array<{
 *   lifnr: string, lieferant_name: string,
 *   netpr: number, peinh: number, waehrung: string,
 *   price_per_unit: number, is_cheapest: boolean,
 *   planned_delivery_time: number, contract_no: string|null, valid_to: string|null
 * }>>}
 */
export const getLagerSuppliers = async (user, matnr, werk) => {
  const qs = new URLSearchParams();
  if (werk) qs.set('werk', werk);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/lager/materials/${encodeURIComponent(matnr)}/suppliers${query}`, 'GET', null, user);
};

/**
 * GET /lager/materials/:matnr/purchase-history?werk=&limit=
 * Letzte Bestellungen fuer ein Material.
 * @param {Object} user
 * @param {string} matnr  - Materialnummer (wird URL-enkodiert)
 * @param {string} werk   - Werk
 * @param {number} [limit=5] - Maximale Anzahl Eintraege
 * @returns {Promise<Array<{
 *   ebeln: string, lieferant_name: string,
 *   menge: number, meins: string,
 *   netpr: number, waehrung: string, bestelldatum: string
 * }>>}
 */
export const getLagerPurchaseHistory = async (user, matnr, werk, limit) => {
  const qs = new URLSearchParams();
  if (werk) qs.set('werk', werk);
  if (limit != null) qs.set('limit', String(limit));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/lager/materials/${encodeURIComponent(matnr)}/purchase-history${query}`, 'GET', null, user);
};

/**
 * GET /lager/materials/:matnr/classifications?werk=
 * Klassifizierungsmerkmale eines Materials (werks-gegated).
 * @param {Object} user
 * @param {string} matnr - Materialnummer (wird URL-enkodiert)
 * @param {string} werk  - Werk (Pflicht, Berechtigungsprüfung)
 * @returns {Promise<Array<{ merkmal_name: string, merkmal_value: string, unit: string|null }>>}
 */
export const getLagerClassifications = async (user, matnr, werk) => {
  const qs = new URLSearchParams();
  if (werk) qs.set('werk', werk);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/lager/materials/${encodeURIComponent(matnr)}/classifications${query}`, 'GET', null, user);
};

// ==================== LAGER-MODUL (Phase 3c) — Klassifizierungs-Filter ====================

/**
 * GET /lager/classification-options?werk=<WERK>[&warengruppe=<CSV>]
 * Liefert Merkmal-Optionen (Text- und Numerik-Merkmale) fuer das gewaehlte Werk,
 * optional eingeschraenkt auf die angegebenen Warengruppen.
 *
 * @param {Object} user
 * @param {string} werk            - Werk (Pflicht)
 * @param {string} [warengruppe]   - CSV der gewaehlten Warengruppen (optional)
 * @returns {Promise<Array<{
 *   merkmal_name: string,
 *   is_numeric: boolean,
 *   values?: string[],
 *   num_min?: number|null,
 *   num_max?: number|null,
 *   unit?: string|null
 * }>>}
 */
export const getLagerClassificationOptions = async (user, werk, warengruppe) => {
  const qs = new URLSearchParams();
  if (werk) qs.set('werk', werk);
  if (warengruppe) qs.set('warengruppe', warengruppe);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/lager/classification-options${query}`, 'GET', null, user);
};

// ==================== LAGER-MODUL (Phase 3b) — Gespeicherte Filter ====================

/**
 * GET /lager/saved-filters
 * Gibt alle gespeicherten Filter des eingeloggten Users zurueck.
 * @param {Object} user
 * @returns {Promise<Array<{ id: number, name: string, filter: Object, created_at: string }>>}
 */
export const getLagerSavedFilters = async (user) => {
  return apiCall('/lager/saved-filters', 'GET', null, user);
};

/**
 * POST /lager/saved-filters
 * Speichert einen neuen Filter fuer den eingeloggten User.
 * @param {Object} user
 * @param {{ name: string, filter: Object }} payload
 * @returns {Promise<{ id: number, name: string, filter: Object, created_at: string }>}
 */
export const createLagerSavedFilter = async (user, payload) => {
  return apiCall('/lager/saved-filters', 'POST', payload, user);
};

/**
 * DELETE /lager/saved-filters/:id
 * Loescht einen gespeicherten Filter (nur eigene, IDOR-sicher).
 * @param {Object} user
 * @param {number} id
 * @returns {Promise<{ message: string }>}
 */
export const deleteLagerSavedFilter = async (user, id) => {
  return apiCall(`/lager/saved-filters/${id}`, 'DELETE', null, user);
};

// ==================== LAGER-MODUL (Phase 4c) — ATP / Verfuegbarkeit ====================

/**
 * GET /lager/materials/:matnr/supply-demand?werk=
 * Liefert die Bedarfs-/Bestandsplanung (MD04-Stil) fuer ein Material/Werk.
 *
 * @param {Object} user
 * @param {string} matnr - Materialnummer (wird URL-enkodiert)
 * @param {string} werk  - Werk (Pflicht, sonst 400)
 * @returns {Promise<{
 *   matnr: string,
 *   werk: string,
 *   bestand: number,
 *   zugaenge: Array<{ element_typ: string, beleg: string, menge: number, termin: string }>,
 *   abgaenge: Array<{ element_typ: string, beleg: string, menge: number, termin: string }>,
 *   summe_zugaenge: number,
 *   summe_abgaenge: number,
 *   verfuegbar_netto: number
 * }>}
 */
export const getLagerSupplyDemand = async (user, matnr, werk) => {
  const qs = new URLSearchParams();
  if (werk) qs.set('werk', werk);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/lager/materials/${encodeURIComponent(matnr)}/supply-demand${query}`, 'GET', null, user);
};

// ==================== LAGER-MODUL (Phase 4) — Dashboard ====================

/**
 * GET /lager/dashboard?werk=
 * KPI-Dashboard fuer das Lager-Modul.
 * @param {Object} user
 * @param {string} werk
 * @returns {Promise<{
 *   kpis: Object,
 *   bestandsverlauf: { source: string, data: Array },
 *   durchlaufzeiten: { source: string, data: Array },
 *   top_materialien: { source: string, data: Array },
 *   aktivitaeten: { source: string, data: Array }
 * }>}
 */
export const getLagerDashboard = async (user, werk) => {
  const qs = new URLSearchParams();
  if (werk) qs.set('werk', werk);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/lager/dashboard${query}`, 'GET', null, user);
};

// ==================== LAGER-MODUL — Export ====================

/**
 * Loeser einen Blob-Download mit Auth-Header aus.
 * Parst den Dateinamen aus dem Content-Disposition-Header (Fallback: fallbackName).
 * @param {string} url        Vollstaendige /api/... URL inkl. Query-String
 * @param {string} token      JWT Bearer Token
 * @param {string} fallbackName  Dateiname wenn Content-Disposition fehlt
 * @returns {Promise<void>}
 */
const triggerBlobDownload = async (url, token, fallbackName) => {
  const res = await fetch(url, {
    headers: (token && !token.startsWith('mock-')) ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}${errText ? ': ' + errText : ''}`);
  }
  // Leer-Signal (0 Datenzeilen): nichts herunterladen, Aufrufer informieren
  if (res.headers.get('x-row-count') === '0') {
    return { empty: true };
  }
  const blob = await res.blob();
  // Dateiname aus Content-Disposition parsen
  let filename = fallbackName;
  const cd = res.headers.get('content-disposition') || '';
  const matchUtf8 = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const matchPlain = cd.match(/filename="([^"]+)"/i) || cd.match(/filename=([^;]+)/i);
  if (matchUtf8 && matchUtf8[1]) {
    filename = decodeURIComponent(matchUtf8[1].trim());
  } else if (matchPlain && matchPlain[1]) {
    filename = matchPlain[1].trim();
  }
  // Temporaeres <a> fuer Download (ObjectURL in try/finally freigeben)
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  try {
    document.body.appendChild(a);
    a.click();
  } finally {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(objectUrl);
  }
  return { empty: false, filename };
};

/**
 * GET /api/purchase-requests/export?year=&status=&ekgrp=&matkl=&kostl=
 * Lädt die (gefilterten) BANF-Anfragen als .xlsx herunter (chronologisch). KPI-Dashboard F14.
 * @returns {Promise<void>}  Löst Download aus, gibt nichts zurück.
 */
export const downloadBanfExport = async (user, filters = {}) => {
  const qs = new URLSearchParams();
  ['year', 'status', 'ekgrp', 'matkl', 'kostl'].forEach((k) => {
    const v = filters[k];
    const val = Array.isArray(v) ? v.join(',') : v;       // Mehrfachauswahl -> CSV
    if (val) qs.set(k, val);
  });
  const today = new Date().toISOString().slice(0, 10);
  await triggerBlobDownload(`/api/purchase-requests/export?${qs.toString()}`, user?.token || '', `Beschaffung_Kennzahlen_${today}.xlsx`);
};

/**
 * GET /api/lager/materials/export?werk=&<alle Filter>
 * Laedt die aktuelle Trefferliste als .xlsx herunter.
 * @param {Object} user
 * @param {{
 *   werk: string,
 *   search?: string,
 *   warengruppe?: string,
 *   materialart?: string,
 *   bestandsstatus?: string,
 *   lagerort?: string,
 *   beschaffungsart?: string,
 *   dispomerkmal?: string,
 *   bestand_min?: string,
 *   bestand_max?: string,
 *   offene_banf?: string,
 *   offene_bestellung?: string,
 *   handlungsbedarf?: boolean,
 *   kritisch?: boolean,
 *   merkmale?: Array,
 *   sort?: string,
 * }} params
 * @returns {Promise<void>}  Loest Download aus, gibt nichts zurueck.
 */
export const downloadLagerMaterialsExport = async (user, params = {}) => {
  const qs = new URLSearchParams();
  if (params.werk) qs.set('werk', params.werk);
  if (params.search) qs.set('search', params.search);
  if (params.warengruppe) qs.set('warengruppe', params.warengruppe);
  if (params.materialart) qs.set('materialart', params.materialart);
  if (params.bestandsstatus && params.bestandsstatus !== 'alle') qs.set('bestandsstatus', params.bestandsstatus);
  if (params.lagerort) qs.set('lagerort', params.lagerort);
  if (params.beschaffungsart) qs.set('beschaffungsart', params.beschaffungsart);
  if (params.dispomerkmal) qs.set('dispomerkmal', params.dispomerkmal);
  if (params.bestand_min != null && params.bestand_min !== '') qs.set('bestand_min', String(params.bestand_min));
  if (params.bestand_max != null && params.bestand_max !== '') qs.set('bestand_max', String(params.bestand_max));
  if (params.offene_banf && params.offene_banf !== 'alle') qs.set('offene_banf', params.offene_banf);
  if (params.offene_bestellung && params.offene_bestellung !== 'alle') qs.set('offene_bestellung', params.offene_bestellung);
  if (params.handlungsbedarf === true) qs.set('handlungsbedarf', 'true');
  if (params.stock_type) qs.set('stock_type', params.stock_type);
  if (params.vendor) qs.set('vendor', params.vendor);
  if (params.ekgrp) qs.set('ekgrp', params.ekgrp);
  if (params.sperrbestand === true || params.sperrbestand === 'true') qs.set('sperrbestand', 'true');
  if (params.qmbestand === true || params.qmbestand === 'true') qs.set('qmbestand', 'true');
  if (params.kritisch === true) qs.set('kritisch', 'true');
  if (Array.isArray(params.merkmale) && params.merkmale.length > 0) {
    qs.set('merkmale', JSON.stringify(params.merkmale));
  }
  if (params.sort) qs.set('sort', params.sort);
  if (params.report) qs.set('report', params.report);
  const today = new Date().toISOString().slice(0, 10);
  const fallback = `Lagerbestand_${params.werk || 'Export'}_${today}.xlsx`;
  return triggerBlobDownload(`/api/lager/materials/export?${qs.toString()}`, user?.token || '', fallback);
};

/**
 * GET /api/lager/special-stock/export?werk=
 * Lädt alle Sonderbestände (Konsignation + Beistellung/Lohnbearbeitung) eines Werks
 * als .xlsx — eine Zeile je Position mit Art/SOBKZ, Lieferant, Lagerort, Mengen.
 * @returns {Promise<{empty:boolean, filename?:string}>}
 */
export const downloadLagerSpecialStockExport = async (user, params = {}) => {
  const qs = new URLSearchParams();
  if (params.werk) qs.set('werk', params.werk);
  const today = new Date().toISOString().slice(0, 10);
  const fallback = `Sonderbestaende_${params.werk || 'Export'}_${today}.xlsx`;
  return triggerBlobDownload(`/api/lager/special-stock/export?${qs.toString()}`, user?.token || '', fallback);
};

/**
 * GET /api/lager/materials/:matnr/export?werk=
 * Laedt das 7-Sheet-Detail-Workbook eines Materials als .xlsx herunter.
 * @param {Object} user
 * @param {string} matnr - Materialnummer
 * @param {string} werk  - Werk
 * @returns {Promise<void>}  Loest Download aus, gibt nichts zurueck.
 */
export const downloadLagerMaterialDetailExport = async (user, matnr, werk) => {
  const qs = new URLSearchParams();
  if (werk) qs.set('werk', werk);
  const today = new Date().toISOString().slice(0, 10);
  const safematnr = String(matnr).replace(/[^a-zA-Z0-9_-]/g, '_');
  const fallback = `Material_${safematnr}_${today}.xlsx`;
  await triggerBlobDownload(
    `/api/lager/materials/${encodeURIComponent(matnr)}/export?${qs.toString()}`,
    user?.token || '',
    fallback,
  );
};

// ==================== WAREHOUSE ====================

/**
 * GET /warehouse/stock
 * @param {Object} user - { token, role, ... }
 * @param {{ q?: string, plant?: string, storage_location?: string }} params - Optionale Filter
 * @returns {Promise<Array>} Array<StockRow>
 */
export const getWarehouseStock = async (user, params = {}) => {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.plant) qs.set('plant', params.plant);
  if (params.storage_location) qs.set('storage_location', params.storage_location);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/warehouse/stock${query}`, 'GET', null, user);
};

/**
 * GET /warehouse/stock/:id
 * @param {Object} user
 * @param {number|string} id - Lagerbestand-ID
 * @returns {Promise<{ stock: StockRow, movements: Movement[] }>}
 */
export const getWarehouseStockDetail = async (user, id) => {
  return apiCall(`/warehouse/stock/${encodeURIComponent(id)}`, 'GET', null, user);
};

/**
 * POST /warehouse/goods-receipt
 * @param {Object} user
 * @param {{
 *   product_id?: number,
 *   matnr?: string,
 *   material_name?: string,
 *   plant: string,
 *   storage_location: string,
 *   quantity: number,
 *   unit?: string,
 *   value_amount?: number,
 *   currency?: string,
 *   reference?: string,
 *   event_id?: string
 * }} payload
 * @returns {Promise<{ success: boolean, idempotent: boolean, message: string, movement_id: number }>}
 */
export const postGoodsReceipt = async (user, payload) => {
  return apiCall('/warehouse/goods-receipt', 'POST', payload, user);
};

/**
 * GET /warehouse/movements
 * @param {Object} user
 * @param {{ product_id?: number, matnr?: string, plant?: string, storage_location?: string, limit?: number }} params
 * @returns {Promise<Array>} Movement[]
 */
export const getStockMovements = async (user, params = {}) => {
  const qs = new URLSearchParams();
  if (params.product_id != null) qs.set('product_id', String(params.product_id));
  if (params.matnr) qs.set('matnr', params.matnr);
  if (params.plant) qs.set('plant', params.plant);
  if (params.storage_location) qs.set('storage_location', params.storage_location);
  if (params.limit != null) qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/warehouse/movements${query}`, 'GET', null, user);
};

/**
 * GET /warehouse/locations
 * @param {Object} user
 * @returns {Promise<Array<{ id: number, plant: string, storage_location: string, location_name: string }>>}
 */
export const getWarehouseLocations = async (user) => {
  return apiCall('/warehouse/locations', 'GET', null, user);
};

// ==================== SAP-ADAPTER (Diagnose, Admin-only) ====================

/**
 * GET /adapter/ping
 * Konnektivitäts-Smoke-Test gegen den aktiven SAP-Konnektor (SAP-ADAPTER-MATRIX.md §9).
 * @param {Object} user
 */
export const pingAdapter = async (user) => {
  return apiCall('/adapter/ping', 'GET', null, user);
};

/**
 * GET /adapter/banf/:banfn
 * Liest eine reale Bestellanforderung (BAPI_PR_GETDETAIL, read-only).
 * @param {Object} user
 * @param {string} banfn
 */
export const getAdapterBanf = async (user, banfn) => {
  return apiCall(`/adapter/banf/${encodeURIComponent(banfn)}`, 'GET', null, user);
};

/**
 * POST /adapter/banf
 * Dry-run BAPI_PR_CREATE (TESTRUN='X' serverseitig fest verdrahtet, legt nie eine echte BANF an).
 * Kostenstellen-Szenario (KNTTP=K), s. SAP-ADAPTER-MATRIX.md §9.
 * @param {Object} user
 * @param {{ekgrp:string, sakto:string, kostl:string, short_text:string, menge:string, meins:string, preis:string, peinh?:string}} fields
 */
export const createAdapterBanfTestrun = async (user, fields) => {
  return apiCall('/adapter/banf', 'POST', fields, user);
};

/**
 * POST /adapter/banf/real
 * ECHTER Schreibvorgang — legt eine reale BANF in SAP an (BAPI_PR_CREATE + COMMIT, kein TESTRUN).
 * Kein Undo. Erfordert einen expliziten Bestätigungs-Token, den nur die UI nach eigener
 * Zwei-Schritt-Bestätigung setzt (s. AdapterTestPanel.js) — niemals ungefragt aufrufen.
 * @param {Object} user
 * @param {{ekgrp:string, sakto:string, kostl:string, short_text:string, menge:string, meins:string, preis:string, peinh?:string}} fields
 */
export const createAdapterBanfReal = async (user, fields) => {
  return apiCall('/adapter/banf/real', 'POST', { ...fields, confirm: 'yes-create-real-banf' }, user);
};

/**
 * POST /adapter/banf/delete
 * Setzt/entfernt das Löschkennzeichen (EBAN-LOEKZ via BAPI_PR_CHANGE) — reversibel, anders als
 * createAdapterBanfReal. `testrun:true` schreibt nichts und braucht keinen Bestätigungs-Token; ein
 * echtes Löschen erfordert denselben Zwei-Schritt-Ablauf wie das echte Anlegen.
 * @param {Object} user
 * @param {{banfn:string, item?:string, testrun?:boolean}} fields
 */
export const deleteAdapterBanf = async (user, fields) => {
  const body = fields.testrun
    ? { ...fields, testrun: 'X' }
    : { ...fields, confirm: 'yes-delete-banf' };
  return apiCall('/adapter/banf/delete', 'POST', body, user);
};

/**
 * GET /adapter/ab/ping?mode=zrest|soap
 * AB-Modul-POC (Auftragsbestaetigung): Konnektivitaets-Vergleich Z-REST vs. SOAP, read-only.
 * @param {Object} user
 * @param {'zrest'|'soap'} mode
 */
export const pingAbAdapter = async (user, mode) => {
  return apiCall(`/adapter/ab/ping?mode=${encodeURIComponent(mode)}`, 'GET', null, user);
};

/**
 * GET /adapter/ab/orders/:ebeln?mode=zrest|soap
 * Read-only PO-Zeilen ueber den gewaehlten Publikationsweg (kein confirmOrder — bewusst nur lesend).
 * @param {Object} user
 * @param {'zrest'|'soap'} mode
 * @param {string} ebeln
 */
export const getAbAdapterOrder = async (user, mode, ebeln) => {
  return apiCall(`/adapter/ab/orders/${encodeURIComponent(ebeln)}?mode=${encodeURIComponent(mode)}`, 'GET', null, user);
};

// ==================== SAP-KONNEKTIVITAETS-KONFIGURATION (Admin-CRUD) ====================

export const getSapCustomers = async (user) => apiCall('/adapter/connectivity/customers', 'GET', null, user);

export const createSapCustomer = async (user, fields) =>
  apiCall('/adapter/connectivity/customers', 'POST', fields, user);

export const updateSapCustomerDetails = async (user, customerId, fields) =>
  apiCall(`/adapter/connectivity/customers/${customerId}/details`, 'PUT', fields, user);

export const getSapSystems = async (user, customerId) =>
  apiCall(`/adapter/connectivity/customers/${customerId}/systems`, 'GET', null, user);

export const createSapSystem = async (user, customerId, fields) =>
  apiCall(`/adapter/connectivity/customers/${customerId}/systems`, 'POST', fields, user);

export const updateSapSystem = async (user, systemId, fields) =>
  apiCall(`/adapter/connectivity/systems/${systemId}`, 'PUT', fields, user);

export const getSapConnections = async (user, systemId) =>
  apiCall(`/adapter/connectivity/systems/${systemId}/connections`, 'GET', null, user);

export const createSapConnection = async (user, systemId, fields) =>
  apiCall(`/adapter/connectivity/systems/${systemId}/connections`, 'POST', fields, user);

export const updateSapConnection = async (user, connectionId, fields) =>
  apiCall(`/adapter/connectivity/connections/${connectionId}`, 'PUT', fields, user);

export const testSapConnection = async (user, connectionId) =>
  apiCall(`/adapter/connectivity/connections/${connectionId}/test`, 'POST', null, user);

// Mock-API für Demo-Zwecke
export const mockApiResponse = (endpoint, method, data) => {
  if (endpoint === '/oci-shops' && method === 'GET') {
    return [
      {
        id: 1,
        name: 'Federnshop.com',
        supplier: 'Federnshop GmbH',
        baseUrl: 'https://www.federnshop.com/de/account/ociautologin.html',
        description: 'Federn, Dämpfer, Fahrwerksteile',
        category: 'Automotive',
        icon: '🚗',
        username: '',
        password: '',
        isActive: true
      }
    ];
  }
  
  if (endpoint === '/admin/users' && method === 'GET') {
    return [
      {
        id: 1,
        username: 'admin',
        email: 'admin@gineex.de',
        role: 'admin',
        isActive: true,
        lastLogin: '2024-06-24 10:30:00'
      }
    ];
  }
  
  return { success: true, data: data };
};